import * as React from 'react';

const load = (storageKey: string): Set<string> => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id): id is string => typeof id === 'string'));
    }
  } catch {
    // Corrupt storage — fall through to empty
  }
  return new Set();
};

/**
 * Collapsed/expanded state for a list of sections, persisted to
 * localStorage so it survives reloads. Client-side only by design.
 */
export const useCollapsedSections = (
  storageKey: string,
): {isCollapsed: (id: string) => boolean; toggle: (id: string) => void} => {
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() =>
    load(storageKey),
  );

  const toggle = React.useCallback(
    (id: string) => {
      setCollapsed((prev) => {
        // Re-read storage so multiple hook instances sharing a key (one per
        // section component) don't clobber each other's persisted entries.
        const next = load(storageKey);
        if (prev.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        try {
          window.localStorage.setItem(storageKey, JSON.stringify([...next]));
        } catch {
          // Storage unavailable — state still works for this session
        }
        return next;
      });
    },
    [storageKey],
  );

  const isCollapsed = React.useCallback(
    (id: string) => collapsed.has(id),
    [collapsed],
  );

  return {isCollapsed, toggle};
};
